import axios from 'axios';
import type { Method } from 'axios';
import type {
  ProcessCatalogItemDTO,
  ProcessCatalogPageDTO,
  ProcessTaskSummaryPageDTO
} from '@krafta/shared-types';

import {
  buildProcessEngineUrl,
  PROCESS_ENGINE_API_BASE,
  processEngineHttp
} from '@shared/api/processEngineClient';
import { readApiErrorBody, readApiErrorMessage } from '@shared/api/httpClient';
import type { GraphSource } from '../rete/serialization';

type ApiRequestOptions = {
  method: Method;
  url: string;
  query?: Record<string, unknown>;
  body?: unknown;
  headers?: Record<string, string>;
};

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly body: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const encodePathParam = (value: string) => encodeURIComponent(value);

const processDefinitionPath = (id: string) =>
  `/mes/processes/definitions/${encodePathParam(id)}`;

const processTaskPath = (id: string) => `/mes/processes/tasks/${encodePathParam(id)}`;
const processInstancePath = (id: string) => `/mes/processes/instances/${encodePathParam(id)}`;

const serializeQuery = (params: Record<string, unknown> = {}) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    searchParams.set(key, Array.isArray(value) ? value.join(',') : String(value));
  });

  return searchParams.toString();
};

const apiRequest = async <T>({
  method,
  url,
  query,
  body,
  headers
}: ApiRequestOptions): Promise<T> => {
  try {
    const response = await processEngineHttp.request<T>({
      method,
      url: buildProcessEngineUrl(url),
      params: query,
      paramsSerializer: { serialize: serializeQuery },
      data: body,
      headers
    });

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new ApiError(
        readApiErrorMessage(error, error.message || 'Ошибка запроса'),
        error.response?.status ?? 0,
        readApiErrorBody(error)
      );
    }

    throw error;
  }
};

export type ProcessDefinitionStatus = 'DRAFT' | 'TEST' | 'PRODUCTION' | 'ARCHIVED';

export type ProcessDefinition = {
  id: string;
  name: string;
  code: string;
  version: number;
  status: ProcessDefinitionStatus;
  isLatest: boolean;
  graphSource: GraphSource;
  executionSnapshot?: Record<string, unknown> | null;
  executionSnapshotHash?: string | null;
  inputSchema?: Record<string, unknown> | null;
  publishedAt?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type ProcessDefinitionSummary = ProcessCatalogItemDTO;

export type ProcessValidationIssueSeverity = 'error' | 'warning' | 'info';

export type ProcessValidationIssue = {
  code: string;
  severity: ProcessValidationIssueSeverity;
  message: string;
  targetType: 'graph' | 'node' | 'connection';
  nodeId?: string;
  connectionId?: string;
  connection?: {
    source: string;
    target: string;
  };
};

export type ProcessPreflightResult = {
  ok: boolean;
  canSave: boolean;
  canPublish: boolean;
  issues: ProcessValidationIssue[];
  summary: {
    nodes: number;
    connections: number;
    errors: number;
    warnings: number;
    info: number;
    hasStart: boolean;
    hasEnd: boolean;
    hasWarehouse: boolean;
    hasManufacturing: boolean;
    hasQualityControl: boolean;
  };
};

export type ProcessDefinitionChangeLogEntry = {
  id: string;
  definitionId: string;
  version: number;
  status: ProcessDefinitionStatus;
  title: string;
  description: string;
  createdAt: string;
  updatedAt: string;
};

export type ProcessAuditEvent = {
  id: string;
  action: string;
  entityType: string;
  entityId?: string | null;
  actorUserId?: string | null;
  actorRoles?: string[];
  outcome: string;
  message?: string | null;
  payload?: Record<string, unknown> | null;
  createdAt: string;
};

export type ProcessRolloutState = {
  enabled: boolean;
  allowedRoles: string[];
  currentUser: {
    id: string | null;
    roles: string[];
    permissions: Record<string, boolean>;
  };
  matrix: Array<{
    permission: string;
    label: string;
    roles: string[];
  }>;
};

export type ProcessSimulationMaterialRow = {
  id: string;
  itemId: string;
  qty: number;
  unit: string;
  direction: 'input' | 'output';
};

export type ProcessSimulationStockItem = {
  itemId: string;
  requiredQty: number;
  onHand: number;
  reserved: number;
  available: number;
  shortage: number;
  covered: boolean;
  warehouseId?: string;
  locationId?: string;
};

export type ProcessSimulationResult = {
  definitionId: string;
  definitionVersion: number;
  parameters: Record<string, unknown>;
  preflight: ProcessPreflightResult;
  stockCheck: {
    ok: boolean;
    items: ProcessSimulationStockItem[];
  };
  route: Array<{ nodeId: string; label: string; type: string }>;
  nodes: Array<{
    nodeId: string;
    label: string;
    type: string;
    operationType?: unknown;
    stationId?: unknown;
    workplaceId?: unknown;
    durationMinutes?: unknown;
    instructions?: string;
    inputs: ProcessSimulationMaterialRow[];
    outputs: ProcessSimulationMaterialRow[];
    branches: Array<{
      connectionId?: string;
      targetNodeId: string;
      label?: string;
      semanticType?: string;
      sourceOutput?: string;
    }>;
  }>;
  materialSummary: Array<{
    itemId: string;
    inputQty: number;
    outputQty: number;
    unit: string;
  }>;
  bomSummary?: Array<{
    bomItemId: string;
    itemId: string;
    requiredQty: number;
    unit: string;
    source: string;
  }>;
  kitting?: {
    requiredItems: number;
    reservedOnReady: boolean;
    shortageCount: number;
  };
};

export type CopyTemplateProcessDefinitionPayload = {
  name?: string;
  code?: string;
  parameterOverrides?: Record<string, string | number | boolean | null>;
};

type ProcessInstance = {
  id?: string;
  [key: string]: unknown;
};

export type ProcessRuntimeInstanceStatus =
  | 'PENDING'
  | 'RUNNING'
  | 'PAUSED'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'FAILED';

export type ProcessRuntimeInstance = {
  id: string;
  definitionId: string;
  definitionVersion?: number;
  executionSnapshotHash?: string | null;
  status: ProcessRuntimeInstanceStatus;
  currentState?: string | null;
  context?: Record<string, unknown> | null;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
};

const startProcessInstance = (
  definitionId: string,
  payload?: Record<string, unknown>
): Promise<ProcessInstance> =>
  apiRequest({
    method: 'POST',
    url: `${processDefinitionPath(definitionId)}/instances`,
    body: { payload }
  });

export const ProcessDefinitionService = {
  getAll(): Promise<ProcessDefinition[]> {
    return apiRequest({
      method: 'GET',
      url: '/mes/processes/definitions',
    });
  },
  getCatalog(params?: {
    cursor?: string;
    kind?: 'process' | 'template';
    limit?: number;
    productType?: string;
    q?: string;
    statuses?: ProcessDefinitionStatus[];
  }): Promise<ProcessCatalogPageDTO> {
    return apiRequest({
      method: 'GET',
      url: '/mes/processes/definitions/catalog',
      query: params
    });
  },
  get(id: string): Promise<ProcessDefinition> {
    return apiRequest<ProcessDefinition>({ method: 'GET', url: processDefinitionPath(id) });
  },
  create(payload: {
    name: string;
    code: string;
    status?: ProcessDefinitionStatus;
    graphSource: GraphSource;
    inputSchema?: Record<string, unknown> | null;
  }): Promise<Partial<ProcessDefinition>> {
    return apiRequest({
      method: 'POST',
      url: '/mes/processes/definitions',
      body: payload,
    });
  },
  update(
    id: string,
    payload: {
      name?: string;
      graphSource: GraphSource;
      status?: ProcessDefinitionStatus;
      inputSchema?: Record<string, unknown> | null;
    }
  ): Promise<unknown> {
    return apiRequest({
      method: 'PUT',
      url: processDefinitionPath(id),
      body: payload
    });
  },
  preflight(graphSource: GraphSource): Promise<ProcessPreflightResult> {
    return apiRequest({
      method: 'POST',
      url: '/mes/processes/definitions/preflight',
      body: { graphSource },
    });
  },
  preflightDefinition(id: string, graphSource?: GraphSource): Promise<ProcessPreflightResult> {
    return apiRequest({
      method: 'POST',
      url: `${processDefinitionPath(id)}/preflight`,
      body: graphSource ? { graphSource } : {}
    });
  },
  createVersion(
    id: string,
    payload: {
      name?: string;
      graphSource?: GraphSource;
      inputSchema?: Record<string, unknown> | null;
    } = {}
  ): Promise<Partial<ProcessDefinition>> {
    return apiRequest({
      method: 'POST',
      url: `${processDefinitionPath(id)}/versions`,
      body: payload
    });
  },
  getChangelog(id: string): Promise<ProcessDefinitionChangeLogEntry[]> {
    return apiRequest({
      method: 'GET',
      url: `${processDefinitionPath(id)}/changelog`
    });
  },
  simulateDefinition(
    id: string,
    parameters?: Record<string, unknown>
  ): Promise<ProcessSimulationResult> {
    return apiRequest({
      method: 'POST',
      url: `${processDefinitionPath(id)}/simulate`,
      body: { parameters }
    });
  },
  copyTemplate(
    id: string,
    payload: CopyTemplateProcessDefinitionPayload
  ): Promise<Partial<ProcessDefinition>> {
    return apiRequest({
      method: 'POST',
      url: `${processDefinitionPath(id)}/copy-template`,
      body: payload
    });
  },
  publish(id: string): Promise<Partial<ProcessDefinition>> {
    return apiRequest({ method: 'POST', url: `${processDefinitionPath(id)}/publish` });
  },
  startInstance(id: string, payload?: Record<string, unknown>): Promise<ProcessInstance> {
    return startProcessInstance(id, payload);
  },
  async launchInstance(id: string, payload?: Record<string, unknown>): Promise<ProcessInstance> {
    const instance = await startProcessInstance(id, payload);

    if (instance.id) {
      await apiRequest({
        method: 'POST',
        url: `${processInstancePath(instance.id)}/events`,
        body: {
          type: 'NEXT'
        }
      });
    }

    return instance;
  }
};

export const ProcessInstanceService = {
  list(params: {
    status?: ProcessRuntimeInstanceStatus;
    definitionId?: string;
  } = {}): Promise<ProcessRuntimeInstance[]> {
    return apiRequest({
      method: 'GET',
      url: '/mes/processes/instances',
      query: {
        status: params.status,
        definitionId: params.definitionId
      }
    });
  },
  get(id: string): Promise<ProcessRuntimeInstance> {
    return apiRequest({
      method: 'GET',
      url: processInstancePath(id)
    });
  }
};

export type ProcessAnalyticsMaterialDirection =
  | 'PLANNED_INPUT'
  | 'PLANNED_OUTPUT'
  | 'ACTUAL_CONSUMED'
  | 'ACTUAL_PRODUCED';

export type ProcessAnalyticsDashboard = {
  kpis: {
    plannedInputQty: number;
    actualConsumedQty: number;
    materialDeltaQty: number;
    overconsumptionCount: number;
    qualityOperations: number;
    totalGood: number;
    totalDefect: number;
    avgDefectPercent: number;
    maxDefectPercent: number;
    completedTasks: number;
    avgDurationMinutes: number;
    bottleneckCount: number;
  };
  materials: Array<{
    itemId: string;
    direction: ProcessAnalyticsMaterialDirection;
    qty: number;
    records: number;
  }>;
  quality: {
    operations: number;
    totalGood: number;
    totalDefect: number;
    avgDefectPercent: number;
    maxDefectPercent: number;
  };
  dashboards: {
    highDefectSteps: Array<{
      id: string;
      instanceId: string;
      taskId: string | null;
      nodeId: string;
      totalGood: number;
      totalDefect: number;
      defectPercent: number;
      allowedDefectPercent: number | null;
      exceededByPercent: number | null;
      createdAt: string;
    }>;
    defectReasons: Array<{
      reason: string;
      qty: number;
      records: number;
      examples: Array<{
        instanceId: string;
        taskId: string | null;
        nodeId: string;
      }>;
    }>;
    materialOverconsumption: Array<{
      instanceId: string;
      taskId: string | null;
      nodeId: string;
      itemId: string;
      planned: number;
      actual: number;
      delta: number;
      overconsumptionPercent: number | null;
    }>;
    operationDurations: Array<{
      taskId: string;
      instanceId: string;
      nodeId: string;
      taskType: string;
      assigneeUserId: string | null;
      stationId: string | null;
      workplaceId: string | null;
      machineId: string | null;
      durationMinutes: number;
      thresholdMinutes: number;
      startedAt: string;
      completedAt: string;
    }>;
    operatorEfficiency: Array<{
      operatorId: string;
      completedTasks: number;
      avgDurationMinutes: number;
      maxDurationMinutes: number;
    }>;
    equipmentEfficiency: Array<{
      equipmentId: string;
      completedTasks: number;
      avgDurationMinutes: number;
      maxDurationMinutes: number;
    }>;
    stockForecast: {
      ok: boolean;
      checkedInstances: number;
      shortageCount: number;
      error?: string;
      items: Array<{
        instanceId: string;
        definitionId: string;
        definitionVersion: number;
        status: string;
        itemId: string;
        requiredQty: number;
        onHand: number;
        reserved: number;
        available: number;
        shortage: number;
        covered: boolean;
        warehouseId?: string;
        locationId?: string;
      }>;
    };
  };
};

export const ProcessAnalyticsService = {
  getDashboard(params?: {
    instanceId?: string;
    from?: string;
    to?: string;
    limit?: number;
  }): Promise<ProcessAnalyticsDashboard> {
    return apiRequest({
      method: 'GET',
      url: '/mes/processes/analytics/dashboard',
      query: params
    });
  }
};

export const ProcessOperationsService = {
  getRollout(): Promise<ProcessRolloutState> {
    return apiRequest({
      method: 'GET',
      url: '/mes/processes/operations/rollout'
    });
  },
  getAuditEvents(params?: {
    action?: string;
    entityType?: string;
    entityId?: string;
    actorUserId?: string;
    limit?: number;
  }): Promise<{ events: ProcessAuditEvent[] }> {
    return apiRequest({
      method: 'GET',
      url: '/mes/processes/operations/audit',
      query: params
    });
  }
};

export type WorkerTaskStatus =
  | 'PENDING'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'PAUSED'
  | 'BLOCKED_BY_MATERIALS'
  | 'FAILED'
  | 'CANCELLED'
  | 'ADMIN_CLOSED';

export type WorkerTask = {
  id: string;
  status: WorkerTaskStatus;
  nodeId?: string;
  stepName?: string;
  instanceId?: string;
  definitionId?: string;
  assigneeRoleId?: string | null;
  assigneeUserId?: string | null;
  deadlineAt?: string | null;
  createdAt?: string;
  startedAt?: string | null;
  completedAt?: string | null;
  [key: string]: unknown;
};

export type CompleteTaskPayload = {
  resultData?: Record<string, unknown>;
  selectedBranch?: string;
  selectedBranchComment?: string;
  branchQuantities?: Record<string, number>;
  actualMaterials?: {
    consumed?: Array<{
      itemId: string;
      qty: number;
      locationId?: string;
      warehouseId?: string;
      bomLineId?: string | null;
      bomItemRef?: string | null;
      inventoryVariantId?: string | null;
      skuCode?: string | null;
      displayName?: string | null;
    }>;
    produced?: Array<{
      itemId: string;
      qty: number;
      locationId?: string;
      warehouseId?: string;
      bomLineId?: string | null;
      bomItemRef?: string | null;
      inventoryVariantId?: string | null;
      skuCode?: string | null;
      displayName?: string | null;
    }>;
  };
};

export type TaskActivityType =
  | 'START'
  | 'PAUSE'
  | 'RESUME'
  | 'COMMENT'
  | 'DEVIATION_ADDED'
  | 'SECONDARY_PP_TOGGLED'
  | 'ISSUE_REPORTED'
  | 'MAINTENANCE_REQUEST'
  | 'STOREKEEPER_ISSUE_CONFIRMED'
  | 'STOREKEEPER_RECEIPT_CONFIRMED'
  | 'STOREKEEPER_DEFECT_RECEIVED'
  | 'STOREKEEPER_TRANSFER_REWORK'
  | 'STOREKEEPER_TRANSFER_DISPOSAL'
  | 'QUALITY_APPROVE'
  | 'QUALITY_REJECT'
  | 'QUALITY_BLOCK'
  | 'QUALITY_REPEAT'
  | 'BOSS_OVERRIDE_DEFECT'
  | 'BOSS_FORCE_CLOSE'
  | 'REASSIGN';

export type TaskActivityRecord = {
  id: string;
  type: string;
  createdAt: string;
  payload: Record<string, unknown> | null;
};

export type WorkerTaskAttachment = {
  addedAt: string;
  createdAt: string;
  downloadPath?: string;
  downloadUrl?: string;
  id: string;
  kind: 'photo' | 'file';
  mimeType: string;
  name: string;
  originalName: string;
  serverId: string;
  size: number;
  sizeBytes: number;
  taskId: string;
  type: string;
};

export type TaskExecutionContext = {
  task: WorkerTask & {
    formSchema?: Record<string, unknown> | null;
    taskType?: string;
    assigneeRoleId?: string | null;
  };
  instance: {
    id: string;
    status: string;
    currentState: string | null;
    definitionId: string;
    context: Record<string, unknown> | null;
  };
  operation: {
    nodeId: string;
    nodeType: string;
    title: string;
    description: string;
    instructions: string;
    plannedDurationMin: number | null;
    assigneeRoleId: string | null;
    equipment: string | null;
    qualityRequirements: string;
    specialConditions: string;
    allowedDefectPercent: number | null;
    inputs: Array<{
      id: string;
      itemId: string;
      qty: number;
      unit: string;
      sourceNodeId?: string;
      bomLineId?: string | null;
      bomItemRef?: string | null;
      inventoryVariantId?: string | null;
      skuCode?: string | null;
      displayName?: string | null;
    }>;
    outputs: Array<{
      id: string;
      itemId: string;
      qty: number;
      unit: string;
      destinationNodeId?: string;
      bomLineId?: string | null;
      bomItemRef?: string | null;
      inventoryVariantId?: string | null;
      skuCode?: string | null;
      displayName?: string | null;
    }>;
    contextOutputs: Array<{
      id: string;
      label: string;
      portKey: string;
      properties: Array<{
        key: string;
        label: string;
        value: string;
      }>;
    }>;
    branchOutputs: Array<{
      id: string;
      label: string;
      portKey: string;
      branchKey: string;
      actionLabel: string;
      activityType?: string;
      commentPolicy: 'none' | 'optional' | 'required';
      tone: 'primary' | 'danger' | 'neutral' | 'warning';
    }>;
    quantityPolicy?: {
      mode: 'single' | 'distribution';
      source: 'input' | 'output' | 'manual';
      requireFullDistribution: boolean;
      goodBranchKeys?: string[];
      defectBranchKeys?: string[];
    };
  };
};

export const WorkerService = {
  getTasks(params?: {
    status?: WorkerTaskStatus;
    taskType?: string;
    assigneeUserId?: string;
    myQueue?: boolean;
    instanceId?: string;
    definitionId?: string;
    nodeId?: string;
    limit?: number;
    cursor?: string;
    q?: string;
  }): Promise<ProcessTaskSummaryPageDTO> {
    return apiRequest({
      method: 'GET',
      url: '/mes/processes/tasks/summaries',
      query: {
        status: params?.status,
        taskType: params?.taskType,
        assigneeUserId: params?.assigneeUserId,
        myQueue: params?.myQueue,
        instanceId: params?.instanceId,
        definitionId: params?.definitionId,
        nodeId: params?.nodeId,
        limit: params?.limit ?? 200,
        cursor: params?.cursor,
        q: params?.q
      }
    });
  },
  claimTask(id: string, assigneeUserId?: string): Promise<unknown> {
    return apiRequest({ method: 'POST', url: `${processTaskPath(id)}/claim`, body: { assigneeUserId } });
  },
  getTaskExecutionContext(id: string): Promise<TaskExecutionContext> {
    return apiRequest({
      method: 'GET',
      url: `${processTaskPath(id)}/execution-context`
    });
  },
  getTaskActivity(id: string): Promise<TaskActivityRecord[]> {
    return apiRequest({
      method: 'GET',
      url: `${processTaskPath(id)}/activity`
    });
  },
  createTaskActivity(
    id: string,
    payload: {
      type: TaskActivityType;
      comment?: string;
      payload?: Record<string, unknown>;
    }
  ): Promise<{ status: 'ok' }> {
    return apiRequest({
      method: 'POST',
      url: `${processTaskPath(id)}/activity`,
      body: payload
    });
  },
  async uploadTaskAttachment(id: string, file: File): Promise<WorkerTaskAttachment> {
    const formData = new FormData();
    formData.append('file', file);

    const attachment = await apiRequest<WorkerTaskAttachment>({
      method: 'POST',
      url: `${processTaskPath(id)}/attachments`,
      body: formData
    });

    return enrichWorkerTaskAttachment(attachment);
  },
  async getTaskAttachments(id: string): Promise<WorkerTaskAttachment[]> {
    const attachments = await apiRequest<WorkerTaskAttachment[]>({
      method: 'GET',
      url: `${processTaskPath(id)}/attachments`
    });

    return attachments.map(enrichWorkerTaskAttachment);
  },
  completeTask(id: string, payload: CompleteTaskPayload): Promise<unknown> {
    return apiRequest({
      method: 'POST',
      url: `${processTaskPath(id)}/complete`,
      body: payload
    });
  },
  adminCloseTaskChain(
    id: string,
    payload: { reason?: string } = {}
  ): Promise<{
    status: 'ok';
    manualClosureStatus: 'ADMIN_CLOSED';
    instanceId: string;
    closedAt: string;
    closedTaskIds: string[];
    skippedTaskIds: string[];
    totalTasks: number;
    reason: string | null;
  }> {
    return apiRequest({
      method: 'POST',
      url: `${processTaskPath(id)}/admin-close-chain`,
      body: payload
    });
  },
  deleteProcessInstance(instanceId: string): Promise<void> {
    return apiRequest({
      method: 'DELETE',
      url: processInstancePath(instanceId)
    });
  }
};

const enrichWorkerTaskAttachment = (attachment: WorkerTaskAttachment): WorkerTaskAttachment => ({
  ...attachment,
  downloadUrl:
    attachment.downloadUrl ??
    (attachment.downloadPath ? `${PROCESS_ENGINE_API_BASE}${attachment.downloadPath}` : undefined)
});
